/**
 * Single source of truth for the list of communication channels the CRM
 * understands. Every channel-aware feature (inbox channel-filter pills,
 * consent toggles, channel-icon picker, message provenance line) used
 * to inline its own subset; centralizing means adding a new channel —
 * say WeChat — touches one file instead of five.
 *
 * The shape is intentionally minimal: { key, label }. Page-specific
 * extensions (icon, "has-this-channel?" predicate, default outbound
 * channel) live next to the consumers that need them, but they all
 * agree on the canonical key + display label here.
 */

export interface Channel {
  /** Wire identifier — matches the `channel` column on `messages` and
   *  `conversations`. Never user-visible. */
  key: string;
  /** Display string. Use `channelLabel(key)` instead of the dict if you
   *  have only the key — it falls back to a Title-cased key for
   *  channels we haven't seen yet. */
  label: string;
}

export const CHANNELS: Channel[] = [
  { key: "sms",        label: "SMS" },
  { key: "voice",      label: "Voice" },
  { key: "email",      label: "Email" },
  { key: "whatsapp",   label: "WhatsApp" },
  { key: "facebook",   label: "Facebook" },
  { key: "instagram",  label: "Instagram" },
  { key: "linkedin",   label: "LinkedIn" },
  { key: "twitter",    label: "Twitter / X" },
  { key: "tiktok",     label: "TikTok" },
  { key: "youtube",    label: "YouTube" },
  { key: "pinterest",  label: "Pinterest" },
  { key: "threads",    label: "Threads" },
  { key: "bluesky",    label: "Bluesky" },
  { key: "telegram",   label: "Telegram" },
  { key: "webchat",    label: "Webchat" },
  /* An enquiry someone typed into a form on her website. It is inbound-only -
     she replies by text or email - but it is the first thing they said, so it
     belongs in the thread with a name she recognises. */
  { key: "form",       label: "Website form" },
  { key: "internal_note", label: "Internal note" },
];

/** Lookup dict — kept as a Record so callers can do `LABELS[key]` without
 *  scanning. Generated from CHANNELS so the two can never drift. */
export const CHANNEL_LABELS: Record<string, string> = Object.fromEntries(
  CHANNELS.map((c) => [c.key, c.label])
);

/** Display name for a channel key. Falls back to Title-cased key if the
 *  channel isn't in CHANNELS — useful for forward-compat with channels
 *  the backend emits before the frontend list is updated. */
export function channelLabel(c: string): string {
  return CHANNEL_LABELS[c] ?? c.charAt(0).toUpperCase() + c.slice(1);
}

/**
 * The subset of channels offered as filter pills on the inbox sidebar.
 * Excludes internal_note (those rows shouldn't filter to a "channel" —
 * they appear inside any thread) and the rarer social channels we
 * don't yet have inbound for.
 */
/**
 * The tabs above the inbox list.
 *
 * "that voice tab? And what's app what are they? Will we be able to use those?
 * Maybe remove for now?" — they were filters for channels she has no way to
 * send or receive on, so they could only ever show an empty list. The ones she
 * actually uses are left, and `inboxFilterChannels` below puts any other
 * channel back the moment a real message arrives on it.
 */
const ALWAYS_FILTERS = ["sms", "email"];
const WHEN_USED_FILTERS = ["whatsapp", "instagram", "facebook", "linkedin", "twitter", "voice"];

export const INBOX_FILTER_CHANNELS: Channel[] = [
  { key: "all",        label: "All" },
  ...CHANNELS.filter((c) => ALWAYS_FILTERS.includes(c.key)),
];

/**
 * The tabs to show given what has actually come through. A channel she has
 * never had a message on is not a filter worth a tab; one she has is, even if
 * nobody connected it deliberately.
 */
export function inboxFilterChannels(seen: Iterable<string>): Channel[] {
  const used = new Set(seen);
  return [
    { key: "all", label: "All" },
    ...CHANNELS.filter((c) =>
      ALWAYS_FILTERS.includes(c.key) || (WHEN_USED_FILTERS.includes(c.key) && used.has(c.key))
    ),
  ];
}
